import * as Sentry from '@sentry/react';
import { EnvironmentHelper } from '../Helpers/EnvironmentHelper';

const REDACTED_VALUE = '[REDACTED]';
const SENSITIVE_KEY_MARKERS = [
	'authorization',
	'cookie',
	'setcookie',
	'password',
	'token',
	'secret',
	'clientsecret',
	'apikey',
	'accesstoken',
	'refreshtoken',
];

const normalizeKey = (key: string): string => key.toLowerCase().replace(/[^a-z0-9]/g, '');

const isSensitiveKey = (key: string): boolean => {
	const normalizedKey = normalizeKey(key);

	return SENSITIVE_KEY_MARKERS.some((marker) => normalizedKey.includes(marker));
};

const removeSensitiveHeaderKeys = (payload: Record<string, string> | undefined): Record<string, string> | undefined => {
	if (!payload) {
		return payload;
	}

	return Object.entries(payload).reduce<Record<string, string>>((acc, [key, value]) => {
		if (!isSensitiveKey(key)) {
			acc[key] = value;
		}

		return acc;
	}, {});
};

const sanitizeQueryString = (queryString: string): string => {
	const params = new URLSearchParams(queryString);

	for (const key of Array.from(params.keys())) {
		if (isSensitiveKey(key)) {
			params.set(key, REDACTED_VALUE);
		}
	}

	return params.toString();
};

const sanitizeUrl = (url: string): string => {
	const hashIndex = url.indexOf('#');
	const hasHash = hashIndex >= 0;
	const hash = hasHash ? url.slice(hashIndex) : '';
	const urlWithoutHash = hasHash ? url.slice(0, hashIndex) : url;

	const queryIndex = urlWithoutHash.indexOf('?');
	if (queryIndex < 0) {
		return url;
	}

	const base = urlWithoutHash.slice(0, queryIndex);
	const queryString = urlWithoutHash.slice(queryIndex + 1);
	const sanitizedQueryString = sanitizeQueryString(queryString);

	return `${base}?${sanitizedQueryString}${hash}`;
};

const sanitizePayload = (payload: unknown): unknown => {
	if (payload === null || payload === undefined) {
		return payload;
	}

	if (Array.isArray(payload)) {
		return payload.map((entry) => sanitizePayload(entry));
	}

	if (typeof payload !== 'object') {
		return payload;
	}

	return Object.entries(payload as Record<string, unknown>).reduce<Record<string, unknown>>((acc, [key, value]) => {
		if (!isSensitiveKey(key)) {
			acc[key] = sanitizePayload(value);
		}

		return acc;
	}, {});
};

export const initSentry = (appName: string) => {
	if (EnvironmentHelper.isLocalDev()) {
		return;
	}

	Sentry.init({
		dsn: 'https://07d62a5de2ac038ff68df35300865b99@o4508806554845184.ingest.de.sentry.io/4509162261905488',
		environment: '__ENV__',
		release: '__BUILD_ID__',
		integrations: [Sentry.browserTracingIntegration()],
		tracesSampleRate: 1.0,
		replaysSessionSampleRate: 0,
		replaysOnErrorSampleRate: 0,
		beforeSend: (event) => {
			if (event.request?.headers) {
				event.request.headers = removeSensitiveHeaderKeys(event.request.headers as Record<string, string>);
			}

			if (event.request?.data !== undefined) {
				event.request.data = sanitizePayload(event.request.data);
			}

			if (typeof event.request?.url === 'string') {
				event.request.url = sanitizeUrl(event.request.url);
			}

			if (event.request?.cookies && typeof event.request.cookies === 'object') {
				event.request.cookies = sanitizePayload(event.request.cookies) as Record<string, string>;
			}

			return event;
		},
		beforeBreadcrumb: (breadcrumb) => {
			if (breadcrumb.data && typeof breadcrumb.data === 'object' && !Array.isArray(breadcrumb.data)) {
				breadcrumb.data = sanitizePayload(breadcrumb.data) as Record<string, unknown>;
			}

			return breadcrumb;
		},
		initialScope: (scope) => {
			scope.setTag('client', appName);
			return scope;
		},
	});
};
